import {
  switchMap,
  take,
  distinctUntilChanged,
  delay,
  filter,
} from 'rxjs/operators';
import {
  SensorsAction,
  SensorsActionTypes,
  SensorsState,
  SensorTabs,
} from '../../types/sensors';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';

import { ReannotateDataReason } from '../../types/reannotate-data-capture';

import { Note, NoteAction, Sensor, Capture } from '../../types/api';
import { SORT_EMPTY } from '../../types/sort';
import { actions } from '../index';
import { getRequiredSessionToken } from '../../utils/session-token';
import { SenseiApi } from '../../utils/api';
import {
  fetchCapturesForSensor,
  fetchNotesForSensor,
  fetchSingleSensor,
} from '../../rx-actions/load-sensors';
import { ON_PREM } from '../../utils/environment';
import { Toaster } from '@densityco/ui';

import createRxStore, { ofType, rxDispatch } from '..';

const INITIAL_NOTES: SensorsState['notes'] = {
  resource: RESOURCE_IDLE,
  creation: {
    active: false,
    body: '',
    submitDisabled: false,
  },
  editing: {
    active: false,
  },
  currentlyDeletingNoteId: null,
};

export const initialState: SensorsState = {
  resource: RESOURCE_IDLE,

  activeTab: ON_PREM ? SensorTabs.NOTES : SensorTabs.CAPTURES,

  captures: {
    filters: [],
    searchText: '',
    sort: SORT_EMPTY,
    selectedCaptureIds: [],
    resource: RESOURCE_IDLE,
  },

  notes: INITIAL_NOTES,
  reannotateReason: ReannotateDataReason.DIFFERENT_LOCATION,
};

function reducer(state: SensorsState, action: SensorsAction): SensorsState {
  switch (action.type) {
    case SensorsActionTypes.SENSORS_MOUNT_DETAIL:
      return {
        ...state,
        resource: RESOURCE_IDLE,
        notes: INITIAL_NOTES,
        activeTab: action.initialSelectedTab
          ? action.initialSelectedTab
          : state.activeTab,
      };

    case SensorsActionTypes.SENSORS_UNMOUNT_DETAIL:
      return {
        ...state,
        resource: RESOURCE_IDLE,
      };

    case SensorsActionTypes.SENSORS_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            sensor: action.sensor,
          },
        },
      };

    case SensorsActionTypes.SENSORS_LOAD_START:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };
    case SensorsActionTypes.SENSORS_LOAD_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };
    case SensorsActionTypes.SENSORS_SWITCH_TAB:
      return {
        ...state,
        activeTab: action.tab,
      };
  }

  if (state.resource.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  switch (action.type) {
    case SensorsActionTypes.SENSORS_CAPTURES_LOAD_START:
      return {
        ...state,
        captures: {
          ...state.captures,
          selectedCaptureIds: [],
          resource: RESOURCE_LOADING,
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_LOAD_ERROR:
      return {
        ...state,
        captures: {
          ...state.captures,
          resource: { status: ResourceStatus.ERROR, error: action.error },
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_LOAD_COMPLETE:
      return {
        ...state,
        captures: {
          ...state.captures,
          sort: {
            column: 'Created At',
            direction: 'desc', // default sort by created_at
          },
          resource: {
            status: ResourceStatus.COMPLETE,
            data: action.captures
              .slice()
              .sort((a, b) => b.created_at.localeCompare(a.created_at)),
          },
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_SET_FILTERS:
      return {
        ...state,
        captures: {
          ...state.captures,
          filters: action.filters,
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_SET_SORT:
      return {
        ...state,
        captures: {
          ...state.captures,
          sort: { column: action.column, direction: action.direction },
          resource: {
            status: ResourceStatus.COMPLETE,
            data: action.captures,
          },
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_CHANGE_CHECKS:
      return {
        ...state,
        captures: {
          ...state.captures,
          selectedCaptureIds: action.checked
            ? Array.from(
                new Set([...state.captures.selectedCaptureIds, ...action.ids]),
              )
            : state.captures.selectedCaptureIds.filter(
                (id) => !action.ids.includes(id),
              ),
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_LOAD_START:
      return {
        ...state,
        notes: {
          ...state.notes,
          resource: RESOURCE_LOADING,
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_LOAD_ERROR:
      return {
        ...state,
        notes: {
          ...state.notes,
          resource: { status: ResourceStatus.ERROR, error: action.error },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_LOAD_COMPLETE:
      return {
        ...state,
        notes: {
          ...state.notes,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: action.notes,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CREATION_OPEN:
      return {
        ...state,
        notes: {
          ...state.notes,
          creation: {
            ...state.notes.creation,
            active: true,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CREATION_CLOSE:
      return {
        ...state,
        notes: {
          ...state.notes,
          creation: {
            ...state.notes.creation,
            active: false,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CREATION_UPDATE:
      return {
        ...state,
        notes: {
          ...state.notes,
          creation: {
            ...state.notes.creation,
            body: action.body,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CREATION_SUBMIT:
      return {
        ...state,
        notes: {
          ...state.notes,
          creation: {
            ...state.notes.creation,
            submitDisabled: true,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CREATION_COMPLETE:
      if (state.notes.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      return {
        ...state,
        notes: {
          ...state.notes,
          creation: {
            active: false,
            body: '',
            submitDisabled: false,
          },
          resource: {
            status: ResourceStatus.COMPLETE,
            data: [...state.notes.resource.data, action.note],
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE:
      if (state.notes.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      const note = state.notes.resource.data.find(
        (i) => i.id === action.noteId,
      );
      if (!note) {
        return state;
      }

      return {
        ...state,
        notes: {
          ...state.notes,
          editing: {
            active: true,
            id: note.id,
            body: note.data.body,
            submitDisabled: false,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_UPDATE:
      if (!state.notes.editing.active) {
        return state;
      }

      return {
        ...state,
        notes: {
          ...state.notes,
          editing: {
            ...state.notes.editing,
            body: action.body,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_CANCEL_EDIT_NOTE:
      return {
        ...state,
        notes: {
          ...state.notes,
          editing: {
            active: false,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_SUBMIT_EDIT_NOTE:
      if (state.notes.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      if (!state.notes.editing.active) {
        return state;
      }

      const id = state.notes.editing.id,
        body = state.notes.editing.body;

      return {
        ...state,
        notes: {
          ...state.notes,
          editing: {
            ...state.notes.editing,
            submitDisabled: true,
          },
          resource: {
            status: ResourceStatus.COMPLETE,
            data: state.notes.resource.data.map((i) =>
              i.id === id ? { ...i, data: { ...i.data, body } } : i,
            ),
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_COMPLETE:
      if (!state.notes.editing.active) {
        return state;
      }
      return {
        ...state,
        notes: {
          ...state.notes,
          editing: {
            ...state.notes.editing,
            active: false,
          },
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE:
      return {
        ...state,
        notes: {
          ...state.notes,
          currentlyDeletingNoteId: action.noteId,
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_DISMISS:
      return {
        ...state,
        notes: {
          ...state.notes,
          currentlyDeletingNoteId: null,
        },
      };

    case SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_CONFIRM:
      if (state.notes.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      return {
        ...state,
        notes: {
          ...state.notes,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: state.notes.resource.data.filter(
              (i) => i.id !== action.noteId,
            ),
          },
          currentlyDeletingNoteId: null,
        },
      };

    case SensorsActionTypes.SENSORS_CAPTURES_DELETE_SUCCESS:
      if (state.captures.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      return {
        ...state,
        captures: {
          ...state.captures,
          resource: {
            status: ResourceStatus.COMPLETE,
            data: state.captures.resource.data.filter(
              (i) => !action.captureIds.includes(i.id),
            ),
          },
        },
      };

    case SensorsActionTypes.SENSORS_REANNOTATE_DATA_CAPTURE_CHANGE_REASON:
      return {
        ...state,
        reannotateReason: action.value,
      };

    default:
      return state;
  }
}

const SensorsStore = createRxStore('SensorsStore', initialState, reducer);

// =================================
// SIDE EFFECT: on initial load of the detail view, figure out what request needs to be made and make it to
// load the data.
// =================================
actions
  .pipe(
    ofType(
      SensorsActionTypes.SENSORS_MOUNT_DETAIL,
      SensorsActionTypes.SENSORS_RELOAD_DETAIL,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    fetchSingleSensor(rxDispatch, jwtToken, (action as any).serialNumber).then(
      (sensor) => {
        fetchCapturesForSensor(
          rxDispatch,
          jwtToken,
          sensor.serial_number,
          state.captures.filters,
          state.captures.searchText,
        );
        fetchNotesForSensor(rxDispatch, jwtToken, sensor.serial_number);
      },
    );
  });

// =================================
// SIDE EFFECT: when changing the filters for the captures section, reload the data with the applied filters.
// =================================
actions
  .pipe(
    ofType(SensorsActionTypes.SENSORS_CAPTURES_SET_FILTERS),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    fetchCapturesForSensor(
      rxDispatch,
      jwtToken,
      state.resource.data.sensor.serial_number,
      state.captures.filters,
      state.captures.searchText,
    );
  });

// =================================
// SIDE EFFECT: when a user submits the "add note" form, send a POST to sensei api
// =================================
actions
  .pipe(
    ofType<{ body: string }>(SensorsActionTypes.SENSORS_NOTES_CREATION_SUBMIT),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    SenseiApi.createNote(
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
      'USER_GENERAL',
      { body: action.body },
    )
      .catch((err) =>
        Toaster.showToast({ text: 'Error creating note!', type: 'error' }),
      )
      .then((note) => {
        rxDispatch({
          type: SensorsActionTypes.SENSORS_NOTES_CREATION_COMPLETE,
          note,
        });
        Toaster.showToast({ text: 'Created note.' });
      });
  });

// =================================
// SIDE EFFECT: when a user updates a note, PUT the note on the api
// =================================
actions
  .pipe(
    ofType<{ body: string }>(SensorsActionTypes.SENSORS_NOTES_SUBMIT_EDIT_NOTE),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    if (!state.notes.editing.active) {
      return;
    }

    const noteId = state.notes.editing.id;

    SenseiApi.updateNote(
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
      noteId,
      { body: state.notes.editing.body },
    )
      .catch((err) =>
        Toaster.showToast({ text: 'Error editing note!', type: 'error' }),
      )
      .then((note) => {
        rxDispatch({
          type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_COMPLETE,
          noteId: note.id,
          noteData: note.data,
        });
        Toaster.showToast({ text: 'Edited note.' });
      });
  });

// =================================
// SIDE EFFECT: when a user deletes a note, DELETE the note on the api
// =================================
actions
  .pipe(
    ofType<{ noteId: Note['id'] }>(
      SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_CONFIRM,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    SenseiApi.deleteNote(
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
      action.noteId,
    )
      .catch((err) =>
        Toaster.showToast({ text: 'Error deleting note!', type: 'error' }),
      )
      .then((note) => Toaster.showToast({ text: 'Deleted note.' }));
  });

// =================================
// SIDE EFFECT: when a user clicks on an action in a note, perform the requisite operation
// =================================
actions
  .pipe(
    ofType<{ noteId: Note['id']; noteAction: NoteAction }>(
      SensorsActionTypes.SENSORS_NOTES_CLICK_NOTE_ACTION,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    // Non server based note actions shouldn't cause a request to go to the server
    if (action.noteAction.type !== 'server') {
      return;
    }

    SenseiApi.performNoteAction(
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
      action.noteId,
      action.noteAction.id,
    )
      .then((note) => {
        // Wait a short delay in the hopes that the state machine will transition within this window
        setTimeout(() => {
          Toaster.showToast({ text: 'Performed note action.' });
          rxDispatch({
            type: SensorsActionTypes.SENSORS_NOTES_RELOAD,
          });
        }, 1000);
      })
      .catch((err) =>
        Toaster.showToast({
          text: 'Error performing note action!',
          type: 'error',
        }),
      );
  });

// =================================
// SIDE EFFECT: make a request to sensei api to reload notes when requested.
// =================================
actions
  .pipe(
    ofType(SensorsActionTypes.SENSORS_NOTES_RELOAD),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    fetchNotesForSensor(
      rxDispatch,
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
    );
  });

// =================================
// SIDE EFFECT: transition a sensor from one state to another
// =================================
actions
  .pipe(
    ofType<{ state: Sensor['current_state'] }>(
      SensorsActionTypes.SENSORS_STATE_TRANSITION,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    SenseiApi.transitionSensorState(
      jwtToken,
      `${state.resource.data.sensor.serial_number}`,
      action.state,
    )
      .then(() => {
        Toaster.showToast({ text: 'Performed sensor state transition.' });
      })
      .catch((err) =>
        Toaster.showToast({
          text: 'Error performing sensor state transition!',
          type: 'error',
        }),
      );
  });

// =================================
// SIDE EFFECT: when a user deletes a capture, DELETE the capture on the api
// =================================
actions
  .pipe(
    ofType<{ captureIds: Array<Capture['id']> }>(
      SensorsActionTypes.SENSORS_CAPTURES_DELETE,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }
    SenseiApi.deleteCaptures(jwtToken, action.captureIds)
      .then(() => {
        rxDispatch({
          type: SensorsActionTypes.SENSORS_CAPTURES_DELETE_SUCCESS,
          captureIds: action.captureIds,
        });
        Toaster.showToast({
          text: `Deleted ${action.captureIds.length} ${
            action.captureIds.length === 1 ? 'capture' : 'captures'
          }.`,
        });
      })
      .catch(() => {
        Toaster.showToast({ text: 'Error deleting captures!', type: 'error' });
      });
  });

// =================================
// SIDE EFFECT: when a user sends a capture for reannotation, make a request to sensei api
// =================================
actions
  .pipe(
    ofType<{ captureIds: Array<Capture['id']> }>(
      SensorsActionTypes.SENSORS_CAPTURES_REANNOTATION,
    ),
    switchMap(
      () => SensorsStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }
    SenseiApi.sendCapturesForReannotation(
      jwtToken,
      action.captureIds,
      state.reannotateReason,
    )
      .then(() => {
        Toaster.showToast({
          text: `Sent ${action.captureIds.length} ${
            action.captureIds.length === 1 ? 'capture' : 'captures'
          } for reannotation.`,
        });
      })
      .catch(() => {
        Toaster.showToast({
          text: 'Error sending for reannotation!',
          type: 'error',
        });
      });
  });

export default SensorsStore;
